import { Button } from '@/shared/components/ui/button';
import type { AssessmentPillar, ResponseOption } from '../api/takeApi';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

type PillarScreenProps = {
  pillar: AssessmentPillar;
  index: number; // 0-based
  total: number;
  responseOptions: ResponseOption[];
  answers: Record<string, number>;
  onAnswer: (questionKey: string, value: 0 | 1 | 2) => void;
  onBack: () => void;
  onContinue: () => void;
  isSaving?: boolean;
  saveError?: boolean;
};

export function PillarScreen({
  pillar,
  index,
  total,
  responseOptions,
  answers,
  onAnswer,
  onBack,
  onContinue,
  isSaving = false,
  saveError = false,
}: PillarScreenProps) {
  const allAnswered = pillar.questions.every((q) => answers[q.key] !== undefined);

  return (
    <div className="mx-auto w-full max-w-2xl">
      {/* Stepper */}
      <div className="mb-6 flex items-center justify-center gap-2" aria-label={`Pillar ${index + 1} of ${total}`}>
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all ${
              i === index ? 'w-6 bg-[#026857]' : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>

      <p className="text-center text-xs font-semibold uppercase tracking-wide text-[#026857]">
        Pillar {pillar.num} of {total}
      </p>
      <h2 className="mt-1 text-center font-serif text-2xl text-gray-900">{pillar.name}</h2>
      <p className="mx-auto mt-2 max-w-xl text-center font-serif text-xl leading-relaxed text-gray-700">
        {pillar.framing}
      </p>
      <p className="mx-auto mt-1.5 max-w-xl text-center text-sm leading-relaxed text-gray-500">
        {pillar.blurb}
      </p>

      <div className="mt-8 space-y-4">
        {pillar.questions.map((q) => (
          <div key={q.key} className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="mb-3 text-[15px] leading-relaxed text-gray-800">{q.text}</p>
            <div className="grid grid-cols-3 gap-2">
              {responseOptions.map((opt) => {
                const selected = answers[q.key] === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onAnswer(q.key, opt.value)}
                    aria-pressed={selected}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      selected
                        ? 'border-[#026857] bg-[#026857] text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-[#026857]'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          className={GREEN}
          disabled={!allAnswered || isSaving}
          onClick={onContinue}
        >
          {isSaving ? 'Saving…' : 'Continue'}
        </Button>
      </div>
      {!allAnswered && (
        <p className="mt-2 text-center text-xs text-gray-400">
          Answer all {pillar.questions.length} to continue.
        </p>
      )}
      {saveError && (
        <p className="mt-2 text-center text-sm text-red-600">
          Couldn&apos;t save your answers. Please try again.
        </p>
      )}
    </div>
  );
}
